<template>
  <!-- type=1 Html container：有 children 走遞迴 -->
  <div v-if="renderType === RenderType.HTML && hasChildren">
    <content-data v-for="(child, idx) in children" :key="idx" :node="child" @image-click="emitImageClick" />
  </div>

  <!-- type=1 Html leaf：inline render，讓相鄰文字 <br/> 正常 inline flow -->
  <div v-else-if="renderType === RenderType.HTML" class="wd-inline" v-html="meta" />

  <!-- type=2 img / type=3 yt：leaf media，結構一致只差 tag/attrs，用 config 驅動 -->
  <component :is="mediaTag" v-else-if="renderType === RenderType.MEDIA" ref="mediaRef" v-bind="mediaAttrs" @click="onMediaClick" />

  <!-- type=4 ul / type=5 ol：走 list_v2 遞迴 li（保留巢狀層級）。舊 flat attr.list 已 deprecated，前端不再讀 -->
  <component :is="listTag" v-else-if="renderType === RenderType.LIST">
    <content-data v-for="(listItem, idx) in list" :key="idx" :node="listItem" @image-click="emitImageClick" />
  </component>

  <!-- type=6 li：內含 1/2/3/4/5，走遞迴 -->
  <li v-else-if="renderType === RenderType.LIST_ITEM">
    <content-data v-for="(child, idx) in children" :key="idx" :node="child" @image-click="emitImageClick" />
  </li>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'

import { ProductDetailHtmlType } from '@/view-models/product'
import type { ProductContentElement } from '@/types/api'

interface Props {
  /** 「了解更多」/「購買須知」靜態內容的單一 tree node */
  node: ProductContentElement
}

interface Emits {
  (e: 'image-click', src: string): void
}

enum RenderType {
  HTML,
  MEDIA,
  LIST,
  LIST_ITEM,
}

defineOptions({ name: 'ContentData' })

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const mediaRef = useTemplateRef<HTMLElement>('mediaRef')

// 影片捲出視窗時暫停播放
const { stop: stopObserveMedias } = useIntersectionObserver(
  mediaRef,
  entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting && entry.target instanceof HTMLIFrameElement) {
        entry.target.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), 'https://www.youtube.com')
      }
    })
  },
  {
    rootMargin: '0px',
    threshold: [0],
  }
)

const type = computed(() => props.node.type)
const meta = computed(() => props.node.meta ?? '')
const children = computed(() => props.node.children ?? [])
const hasChildren = computed(() => children.value.length > 0)
const list = computed(() => props.node.attr?.list_v2 ?? [])
const listTag = computed(() => (type.value === ProductDetailHtmlType.OrderedList ? 'ol' : 'ul'))

const mediaTag = computed(() => {
  switch (type.value) {
    case ProductDetailHtmlType.Image:
      return 'img'
    case ProductDetailHtmlType.Youtube:
      return 'iframe'
    default:
      return 'div'
  }
})

const renderType = computed(() => {
  switch (type.value) {
    case ProductDetailHtmlType.Image:
    case ProductDetailHtmlType.Youtube:
      return RenderType.MEDIA
    case ProductDetailHtmlType.UnorderedList:
    case ProductDetailHtmlType.OrderedList:
      return RenderType.LIST
    case ProductDetailHtmlType.ListItem:
      return RenderType.LIST_ITEM
    case ProductDetailHtmlType.Html:
    default:
      return RenderType.HTML
  }
})

const mediaAttrs = computed(() => {
  const attrs: { src: string; class: string; style?: Record<string, unknown>; allowfullscreen?: boolean } = {
    src: '',
    class: '',
  }

  switch (type.value) {
    case ProductDetailHtmlType.Image: {
      const width = props.node.attr?.width ?? 0
      const height = props.node.attr?.height ?? 0

      attrs.src = meta.value
      attrs.class = 'wd-w-full wd-cursor-pointer'
      attrs.style = { 'aspect-ratio': width && height ? `${width}/${height}` : undefined }
      break
    }
    case ProductDetailHtmlType.Youtube: {
      attrs.src = `https://www.youtube.com/embed/${meta.value}?enablejsapi=1`
      attrs.class = 'wd-w-full wd-aspect-video'
      attrs.allowfullscreen = true
      break
    }
  }

  return attrs
})

onUnmounted(() => {
  stopObserveMedias()
})

function emitImageClick(src: string) {
  emit('image-click', src)
}

function onMediaClick() {
  if (type.value === ProductDetailHtmlType.Image) {
    emitImageClick(meta.value)
  }
}
</script>

<style lang="scss" scoped>
/* Vuetify 只對 ul 補 padding（.v-application ul），ol 沒補導致數字標記溢出被裁。對齊 ul 的 24px。 */
ol {
  @apply wd-pl-6;
}
</style>
